import React, { useEffect, useMemo, useRef, useState } from 'react'
import { merge } from 'ramda'
import { useSelector } from 'react-redux'

import { cpf, placa } from '@/consts/regexp'
import theme, { colors } from '@/theme'
import formatter from '@/services/formatter'
import { Yup } from '@/services/yup'
import { toMask } from '@/services/masks'
import { FormTextField } from '@/components/CoForm'
import { DatePickerCollect } from '@/components/DatePicker/CollectDate'
import {
  ComboboxLastDrivers,
  ComboboxLastVehicles,
  ComboboxTruckTypes,
} from '@/components/Combobox'

import {
  FinishForm,
  Margin,
  StyledButton as SendButton,
  SendIcon,
} from '../styles'
import { SelectCarrierDialog } from './SelectCarrierDialog'
import { useBoolean } from '@/hooks/useBoolean'
import { useProfile } from '@/hooks'

// --------------- 𝕄𝕖𝕥𝕒𝕕𝕒𝕥𝕒 ---------------

const initialData = {
  vpt_placa: '',
  id_tipo_caminhao: '',
  vpt_motorista: '',
  vpt_cod_motorista: '',
  vpt_dcoleta: '',
  cod_empresa: null,
  capacity: 0,
  available: 0,
  tare: 0,
}

// --------------- 𝕌𝕥𝕚𝕝𝕤 ---------------

const FormCollectValidationSchema = Yup.object().shape({
  vpt_placa: Yup.string().required('Informe a placa do veiculo'),
  id_tipo_caminhao: Yup.string().required('Informe o tipo do veiculo'),
  vpt_motorista: Yup.string().required('Informe o nome do motorista'),
  vpt_cod_motorista: Yup.string().required('Informe o CPF do motorista'),
  vpt_dcoleta: Yup.string().required('Informe a data de previsão'),
  cod_empresa: Yup.string().nullable(),
})

// --------------- ℂ𝕠𝕞𝕡𝕠𝕟𝕖𝕟𝕥𝕤 ---------------

export function WeightField(props) {
  return (
    <FormTextField
      {...props}
      large
      disabled
      formatValue={(v) => formatter(v).toDecimal()}
    />
  )
}

// --------------- 𝕄𝕒𝕚𝕟 ---------------

export function FormCollect({ isEditing, onSubmit }) {
  const formRef = useRef(null)

  const availableFieldRef = useRef(null)

  const [loading] = useState(false)

  const [sendAvailable, setSendAvailable] = useState(true)

  const user = useProfile()

  const selectCarrierDialogOpen = useBoolean()

  const activeCollectWeight = useSelector(
    (state) => state.users.collections.active.weight
  )

  const activeCollectParams = useSelector(
    (state) => state.users.collections.active.collectParams
  )

  const collects = useSelector(
    (state) => state.users.collections.active.collect
  )

  const vehicles = useSelector((state) => state.comuns.lastVehicles.vehicles)

  const initialValues = useMemo(() => {
    const carrierId = collects[0]?.cod_empresa || null

    return merge(
      { ...initialData, cod_empresa: carrierId },
      activeCollectParams
    )
  }, [activeCollectParams, collects])

  // ----------------------------------------------------------------

  const updateCollectWeightFieldColor = (available) => {
    const collectWithOvercapacity = available < 1

    formRef.current.setFieldError(
      'available',
      collectWithOvercapacity ? 'Capacidade do veículo excedida.' : null
    )

    availableFieldRef.current.style.color = collectWithOvercapacity
      ? theme.palette.error.main
      : colors.color500
  }

  const calcAvailableWeight = (capacity, tare, weight) =>
    Number(capacity - tare - weight)

  const updateAvailableWeight = (capacity) => {
    const tare = formRef.current.getFieldValue('tare')

    const availableWeight = calcAvailableWeight(
      capacity,
      tare,
      activeCollectWeight
    )

    formRef.current.setData({
      capacity,
      available: availableWeight,
    })

    updateCollectWeightFieldColor(availableWeight)
    setSendAvailable(availableWeight > 0)
  }

  // ----------------------------------------------------------------

  function handleSubmit(values) {
    if (
      (user.isCustomer || user.isRepresentative) &&
      !selectCarrierDialogOpen.value
    ) {
      selectCarrierDialogOpen.onTrue()
      return
    }

    onSubmit(
      merge(values, {
        collectWeight: activeCollectWeight,
      })
    )

    selectCarrierDialogOpen.onFalse()
  }

  function handleSave() {
    const values = formRef.current.getValues()

    handleSubmit(values)
  }

  function handleChangeVehicle(_, vehicle) {
    updateAvailableWeight(vehicle?.capacity || 0)
  }

  function handleChangeTruckType(_, truckType) {
    const vehicleId = formRef.current.getFieldValue('vpt_placa')
    const usingVehicleCapacity = vehicles.some((ve) => ve.pla_vei === vehicleId)

    if (!usingVehicleCapacity) {
      updateAvailableWeight(truckType?.capacity || 0)
    }
  }

  function handleChangeDriver(_, driver) {
    formRef.current.setFieldValue('vpt_motorista', driver.label)
    formRef.current.setFieldValue('vpt_cod_motorista', driver.value)
  }

  useEffect(() => {
    const capacity = formRef.current.getFieldValue('capacity')

    updateAvailableWeight(capacity)
  }, [activeCollectWeight])

  return (
    <FinishForm
      formRef={formRef}
      initialData={initialValues}
      onSubmit={handleSubmit}
      schema={FormCollectValidationSchema}
    >
      <span className="title">
        {isEditing ? 'Editar Coleta' : 'Finalizar Coleta'}
      </span>

      <ComboboxLastVehicles
        label="Placa"
        name="vpt_placa"
        inputProps={{
          uppercase: true,
          textMaskProps: toMask(placa.array),
        }}
        onAfterChange={handleChangeVehicle}
      />

      <ComboboxTruckTypes
        label="Tipo do Caminhão"
        name="id_tipo_caminhao"
        loading={loading}
        onAfterChange={handleChangeTruckType}
      />

      <ComboboxLastDrivers
        label="Motorista"
        name="vpt_motorista"
        onAfterChange={handleChangeDriver}
      />

      <FormTextField
        label="CPF do Motorista"
        name="vpt_cod_motorista"
        textMaskProps={toMask(cpf.array)}
      />

      <DatePickerCollect label="Data Prevista para Coleta" name="vpt_dcoleta" />

      <FormTextField
        label="Observação"
        name="vpt_obs"
        multiline
        maxRows={10}
        minRows={5}
      />

      <Margin />

      <WeightField label="Capacidade Total do Veículo" name="capacity" />

      <WeightField
        label="Peso total da Coleta"
        name="total"
        value={activeCollectWeight}
      />

      <WeightField
        label="Disponível para Carregamento"
        name="available"
        inputRef={availableFieldRef}
      />

      <Margin />

      <SendButton
        type="submit"
        label={isEditing ? 'Salvar Alterações' : 'Enviar Coleta'}
        endIcon={<SendIcon />}
        disabled={!sendAvailable}
      />

      <SelectCarrierDialog
        open={selectCarrierDialogOpen.value}
        onClose={selectCarrierDialogOpen.onFalse}
        onSave={handleSave}
      />
    </FinishForm>
  )
}

FormCollect.defaultProps = {
  isEditing: false,
}

export default FormCollect
